/**
 * BayReviews.ProductData
 *
 * Shared helpers for extracting product data from the SuiteCommerce PDP
 * component. Centralizing this logic lets both the entry point and the PDP
 * child views resolve the current product ID at render time.
 */

define('BayReviews.ProductData', ['BayReviews.ScriptLoader'], function (ScriptLoader) {
    'use strict';

    /**
     * Normalize a raw item/model object into a plain data object. Handles both
     * plain objects and Backbone-style models that expose .get() and .attributes.
     */
    function normalizeItem(obj) {
        if (!obj) {
            return {};
        }
        if (typeof obj.get === 'function') {
            var attrs = obj.attributes || {};
            return {
                internalid: obj.get('internalid') || attrs.internalid || obj.get('id') || attrs.id || '',
                itemid: obj.get('itemid') || attrs.itemid || '',
                displayname: obj.get('displayname') || attrs.displayname || '',
                storedisplayname2: obj.get('storedisplayname2') || attrs.storedisplayname2 || ''
            };
        }
        return obj;
    }

    /**
     * Unwrap nested item structures. Some PDP themes return:
     *   - a Backbone model where the real item is in .get('item')
     *   - a plain object like { item: { internalid: ... } }
     * This drills down to the leaf item while guarding against loops.
     */
    function resolveItemSource(item, depth) {
        depth = depth || 0;
        if (!item || depth > 3) {
            return item;
        }

        if (typeof item.get === 'function') {
            var fromGet = item.get('item');
            if (fromGet) {
                return resolveItemSource(fromGet, depth + 1);
            }
            var attrs = item.attributes || {};
            if (attrs.item) {
                return resolveItemSource(attrs.item, depth + 1);
            }
            return item;
        }

        if (item.attributes) {
            if (item.attributes.item) {
                return resolveItemSource(item.attributes.item, depth + 1);
            }
            return item.attributes;
        }

        if (item.item && !item.internalid && !item.id) {
            return resolveItemSource(item.item, depth + 1);
        }

        return item;
    }

    /**
     * Read a product identifier from the current URL. SuiteCommerce product
     * detail pages expose the item id in the query string (?product=140) or in
     * the path (/product/140/...). This is the id the BayEngage widget uses for
     * its API calls, so we prefer it over derived model ids.
     */
    function getUrlProductId() {
        var loc = window.location || {};
        var search = loc.search || '';
        var pathname = loc.pathname || '';
        var hash = loc.hash || '';
        var combined = search + '&' + hash;

        var queryMatch = combined.match(/[?&]product=([^&#]+)/);
        if (queryMatch) {
            return decodeURIComponent(queryMatch[1]);
        }

        var pathMatch = pathname.match(/\/product\/([^/]+)/);
        if (pathMatch) {
            return decodeURIComponent(pathMatch[1]);
        }

        return '';
    }

    /**
     * Extract product data from the PDP component.
     */
    function getPdpProductData(pdp, config) {
        if (!pdp || typeof pdp.getItemInfo !== 'function') {
            return null;
        }

        var item = pdp.getItemInfo();
        if (config && config.debugMode && window.console) {
            window.console.log('BayReviews: pdp.getItemInfo() =', item);
        }
        if (!item) {
            return null;
        }

        var src = normalizeItem(resolveItemSource(item));
        var rawItem = normalizeItem(item);

        var selected = typeof pdp.getSelectedMatrixChilds === 'function'
            ? pdp.getSelectedMatrixChilds()
            : null;
        var child = selected && normalizeItem(Array.isArray(selected) ? selected[0] : selected);

        var urlProductId = getUrlProductId();
        var extractedId = String((child && child.internalid)
            ? child.internalid
            : (src.internalid || rawItem.internalid || ''));

        // Prefer the URL product id because the BayEngage widget derives its API
        // product id from the URL. Fall back to the PDP model id when the URL
        // does not expose one.
        var productId = urlProductId || extractedId;

        if (!productId) {
            if (config && config.debugMode && window.console) {
                window.console.warn('BayReviews: could not resolve productId from PDP item or URL');
            }
            return null;
        }

        // Pick name/SKU from the item whose internalid matches the resolved id.
        var nameSource = src;
        if (child && String(child.internalid) === String(productId)) {
            nameSource = child;
        }

        var productName = String(nameSource.displayname || nameSource.storedisplayname2 || nameSource.itemid || '');
        var productSku = String(nameSource.itemid || '');

        if (config && config.debugMode && window.console) {
            window.console.log('BayReviews: resolved product', {
                urlProductId: urlProductId,
                extractedId: extractedId,
                productId: productId,
                productName: productName,
                productSku: productSku
            });
        }

        return {
            productId: productId,
            productName: productName,
            productSku: productSku
        };
    }

    /**
     * Update the shared payload with PDP product data and sync globals + DOM.
     */
    function applyProductData(payload, productData) {
        if (!payload || !productData) {
            return;
        }

        payload.commonData = payload.commonData || {};
        payload.tbrConfig = payload.tbrConfig || {};

        payload.commonData.productId = productData.productId;
        payload.commonData.productName = productData.productName;
        payload.commonData.productSku = productData.productSku;

        payload.tbrConfig.productId = productData.productId;
        payload.tbrConfig.productName = productData.productName;

        // Sync the global data objects the widget reads. This is required for
        // widgets (such as WriteReviewWidget) that resolve the product id from
        // window.brv.commonData rather than from a placeholder data attribute.
        ScriptLoader.updateGlobals(payload);
        ScriptLoader.updateProductAttributes(productData.productId);
    }

    return {
        normalizeItem: normalizeItem,
        resolveItemSource: resolveItemSource,
        getPdpProductData: getPdpProductData,
        applyProductData: applyProductData
    };
});
